import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TabViewModule } from 'primeng/tabview';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { ServiceOrderTemplateService } from '../../../services/service-order-template.service';
import { ServiceOrderTemplate, ServiceOrderTemplateChecklistItem, ServiceOrderTemplateDto, ServiceOrderTemplateCustomField, ServiceOrderTemplateCustomFieldDto } from '../../../models/service-order-template.model';

@Component({
  selector: 'app-form-service-order-template',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    TabViewModule,
    CheckboxModule,
    DropdownModule,
    ToastModule,
    TooltipModule,
    DialogModule
  ],
  providers: [MessageService],
  templateUrl: './form-service-order-template.component.html'
})
export class FormServiceOrderTemplateComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  id!: number;
  activeTabIndex = 0;
  companyId: number = 1; // TODO: Obter do contexto/auth

  // Checklist items
  checklistItems: ServiceOrderTemplateChecklistItem[] = [];
  
  // Custom Fields (criados no template)
  customFields: ServiceOrderTemplateCustomField[] = [];
  
  // Para criação de novo campo
  showNewFieldModal: boolean = false;
  newFieldLabel: string = '';
  newFieldType: 'TEXT' | 'NUMBER' | 'DATE' | 'TEXTAREA' | 'DROPDOWN' | 'CHECKBOX' = 'TEXT';
  newFieldRequired: boolean = false;
  newFieldOptions: string[] = []; // Para dropdown
  newFieldOptionText: string = ''; // Input temporário para adicionar opção

  fieldTypes = [
    { label: 'Texto', value: 'TEXT' },
    { label: 'Número', value: 'NUMBER' },
    { label: 'Data', value: 'DATE' },
    { label: 'Área de Texto', value: 'TEXTAREA' },
    { label: 'Dropdown', value: 'DROPDOWN' },
    { label: 'Checkbox', value: 'CHECKBOX' }
  ];

  loading = false;

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private templateService: ServiceOrderTemplateService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    
    const idParam = this.activatedRoute.snapshot.paramMap.get('id');
    if (idParam) {
      this.id = parseInt(idParam);
      this.isEditMode = true;
      this.loadTemplate(this.id);
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]]
    });
  }


  loadTemplate(id: number): void {
    this.loading = true;
    console.log('loadTemplate called with id:', id);
    this.templateService.getById(id).subscribe({
      next: (response) => {
        console.log('Template response:', response);
        if (response.success && response.data) {
          const template = response.data;
          console.log('Template data:', template);
          this.form.patchValue({
            name: template.name
          });
          
          // Carregar checklist items
          this.checklistItems = template.checklistItems || [];
          console.log('Checklist items loaded:', this.checklistItems.length);
          
          // Carregar campos personalizados do template
          // Backend pode retornar customFieldDefinitions (completos) ou customFields
          if (template.customFieldDefinitions && template.customFieldDefinitions.length > 0) {
            // Converter CustomFieldDefinition para ServiceOrderTemplateCustomField
            this.customFields = template.customFieldDefinitions.map((def: any, index: number) => {
              // Parse options se for string JSON
              let options: string[] | undefined = undefined;
              if (def.options) {
                if (typeof def.options === 'string') {
                  try {
                    options = JSON.parse(def.options);
                  } catch (e) {
                    console.error('Error parsing options:', e);
                    options = undefined;
                  }
                } else if (Array.isArray(def.options)) {
                  options = def.options;
                }
              }
              
              return {
                id: def.id,
                label: def.label,
                fieldType: def.fieldType,
                required: def.required,
                options: options,
                order: def.order !== undefined ? def.order : index
              };
            });
            console.log('Custom fields loaded from customFieldDefinitions:', this.customFields.length);
          } else if (template.customFields && template.customFields.length > 0) {
            // Normalizar customFields do backend: backend envia "name", front usa "label"; parsear options se for string
            this.customFields = template.customFields.map((f: any, index: number) => {
              let options: string[] | undefined = undefined;
              if (f.options != null) {
                if (typeof f.options === 'string') {
                  try {
                    options = JSON.parse(f.options);
                  } catch (e) {
                    options = undefined;
                  }
                } else if (Array.isArray(f.options)) {
                  options = f.options;
                }
              }
              return {
                id: f.id,
                label: f.label ?? f.name ?? '',
                fieldType: f.fieldType,
                required: f.required ?? false,
                options,
                order: f.order ?? index + 1
              };
            });
            console.log('Custom fields loaded from customFields:', this.customFields.length);
          } else {
            this.customFields = [];
            console.log('No custom fields found');
          }
          console.log('Custom fields data:', this.customFields);
        } else {
          console.warn('Response sem success ou data:', response);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar template:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: 'Não foi possível carregar o template',
          life: 3000
        });
        this.loading = false;
      }
    });
  }

  // Checklist Management
  addChecklistItem(): void {
    const newItem: ServiceOrderTemplateChecklistItem = {
      text: '',
      order: this.checklistItems.length + 1,
      checked: false
    };
    this.checklistItems.push(newItem);
  }

  removeChecklistItem(index: number): void {
    this.checklistItems.splice(index, 1);
    // Reordenar
    this.checklistItems.forEach((item, i) => {
      item.order = i + 1;
    });
  }

  moveItemUp(index: number): void {
    if (index > 0) {
      const temp = this.checklistItems[index];
      this.checklistItems[index] = this.checklistItems[index - 1];
      this.checklistItems[index - 1] = temp;
      // Atualizar ordem
      this.checklistItems.forEach((item, i) => {
        item.order = i + 1;
      });
    }
  }

  moveItemDown(index: number): void {
    if (index < this.checklistItems.length - 1) {
      const temp = this.checklistItems[index];
      this.checklistItems[index] = this.checklistItems[index + 1];
      this.checklistItems[index + 1] = temp;
      // Atualizar ordem
      this.checklistItems.forEach((item, i) => {
        item.order = i + 1;
      });
    }
  }

  // Custom Fields Management
  openNewFieldModal(): void {
    this.showNewFieldModal = true;
    // Resetar formulário
    this.newFieldLabel = '';
    this.newFieldType = 'TEXT';
    this.newFieldRequired = false;
    this.newFieldOptions = [];
    this.newFieldOptionText = '';
  }

  closeNewFieldModal(): void {
    this.showNewFieldModal = false;
    // Limpar formulário
    this.newFieldLabel = '';
    this.newFieldType = 'TEXT';
    this.newFieldRequired = false;
    this.newFieldOptions = [];
    this.newFieldOptionText = '';
  }

  addCustomField(): void {
    if (!this.newFieldLabel.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Digite o label do campo',
        life: 3000
      });
      return;
    }

    // Validar dropdown
    if (this.newFieldType === 'DROPDOWN' && this.newFieldOptions.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Adicione pelo menos uma opção ao dropdown',
        life: 3000
      });
      return;
    }

    const newField: ServiceOrderTemplateCustomField = {
      label: this.newFieldLabel.trim(),
      fieldType: this.newFieldType,
      required: this.newFieldRequired,
      options: this.newFieldType === 'DROPDOWN' ? [...this.newFieldOptions] : undefined,
      order: this.customFields.length + 1
    };

    this.customFields.push(newField);

    // Fechar modal e limpar formulário
    this.closeNewFieldModal();
    
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso!',
      detail: 'Campo adicionado com sucesso',
      life: 2000
    });
  }

  removeCustomField(index: number): void {
    this.customFields.splice(index, 1);
    // Reordenar
    this.customFields.forEach((field, i) => {
      field.order = i + 1;
    });
  }

  moveFieldUp(index: number): void {
    if (index > 0) {
      const temp = this.customFields[index];
      this.customFields[index] = this.customFields[index - 1];
      this.customFields[index - 1] = temp;
      // Atualizar ordem
      this.customFields.forEach((field, i) => {
        field.order = i + 1;
      });
    }
  }

  moveFieldDown(index: number): void {
    if (index < this.customFields.length - 1) {
      const temp = this.customFields[index];
      this.customFields[index] = this.customFields[index + 1];
      this.customFields[index + 1] = temp;
      // Atualizar ordem
      this.customFields.forEach((field, i) => {
        field.order = i + 1;
      });
    }
  }

  // Gerenciamento de opções do dropdown
  addDropdownOption(): void {
    if (this.newFieldOptionText.trim() && !this.newFieldOptions.includes(this.newFieldOptionText.trim())) {
      this.newFieldOptions.push(this.newFieldOptionText.trim());
      this.newFieldOptionText = '';
    }
  }

  removeDropdownOption(index: number): void {
    this.newFieldOptions.splice(index, 1);
  }

  // Form Submission
  onSubmit(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Preencha todos os campos obrigatórios',
        life: 3000
      });
      return;
    }

    // Validar checklist items
    const validChecklistItems = this.checklistItems.filter(item => item.text.trim() !== '');
    if (validChecklistItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Adicione pelo menos um item ao checklist',
        life: 3000
      });
      return;
    }

    this.loading = true;

    // Enviar TODOS os campos personalizados em customFields (lista completa)
    // O backend substitui a lista pelo que recebe; se enviarmos só os novos, os antigos são apagados
    const customFieldsToSend = this.customFields.map((field, index) => {
      let optionsValue: string | undefined = undefined;
      if (field.options) {
        if (Array.isArray(field.options)) {
          optionsValue = JSON.stringify(field.options);
        } else if (typeof field.options === 'string') {
          optionsValue = field.options;
        }
      }
      const item: any = {
        name: field.label || (field as any).name || '',
        fieldType: field.fieldType,
        required: field.required,
        options: optionsValue,
        order: field.order ?? index
      };
      // Incluir id se existir (para o backend manter vínculo com definição existente)
      if (field.id != null) {
        item.id = field.id;
      }
      return item;
    });

    const templateDto: ServiceOrderTemplateDto = {
      name: this.form.value.name,
      description: undefined,
      companyId: this.companyId,
      active: true, // Sempre ativo por padrão
      checklistItems: validChecklistItems.map(item => ({
        text: item.text.trim(),
        order: item.order,
        checked: item.checked || false
      })),
      customFields: customFieldsToSend.length > 0 ? customFieldsToSend : undefined
    };

    console.log('DTO final que será enviado:', JSON.stringify(templateDto, null, 2));

    const operation = this.isEditMode
      ? this.templateService.update(this.id, templateDto)
      : this.templateService.create(templateDto);

    operation.subscribe({
      next: (response) => {
        if (response.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso!',
            detail: `Template ${this.isEditMode ? 'atualizado' : 'criado'} com sucesso`,
            life: 3000
          });
          setTimeout(() => {
            this.router.navigate(['/app/service-order-templates']);
          }, 1000);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao salvar template:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: `Não foi possível ${this.isEditMode ? 'atualizar' : 'criar'} o template`,
          life: 3000
        });
        this.loading = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/app/service-order-templates']);
  }

  get checklistItemsCount(): number {
    return this.checklistItems.filter(item => item.text.trim() !== '').length;
  }

  get customFieldsCount(): number {
    return this.customFields.length;
  }

  getFieldTypeLabel(type: string): string {
    const fieldType = this.fieldTypes.find(ft => ft.value === type);
    return fieldType ? fieldType.label : type;
  }
}

